import React, { useState } from 'react';
import { Row, Col, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faHashtag,
  faCalendarAlt,
  faTachometerAlt,
  faCheckCircle,
  faArrowTrendUp,
  faArrowTrendDown,
  faClock,
  faStopwatch,
  faArrowRight
} from '@fortawesome/free-solid-svg-icons';
import { useLongestActiveTickets } from '../../hooks/useLongestActiveTickets';
import InProgressTicketsModal from './InProgressTicketsModal';
import TimeMetricCard from './TimeMetricCard';

const formatDuration = minutes => {
  if (!minutes || minutes <= 0) return '0 min';
  if (minutes < 60) return `${minutes} min`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
};

const MetricsCards = ({ data, isLoading = false, agentId = '' }) => {
  const [showModal, setShowModal] = useState(false);
  const {
    tickets: inProgressTickets,
    page,
    totalPages,
    totalRegistros,
    isLoading: isLoadingTickets,
    goToNextPage,
    goToPrevPage,
    goToPage
  } = useLongestActiveTickets(agentId);

  const handleOpenTicket = ticketId => {
    window.open(`/support-engine/ticket/${ticketId}`, '_blank');
  };

  const totalHoy = data?.iTotalHoy ?? 0;
  const totalAyer = data?.iTotalAyer ?? 0;
  const pendientes = data?.iPendientes ?? 0;
  const enProgreso = data?.iEnProgreso ?? 0;
  const resueltosHoy = data?.iResueltosHoy ?? 0;
  const totalHistorico = data?.iTotalHistorico ?? 0;
  const totalResueltos = data?.iTotalResueltos ?? 0;
  const tiempoRespuestaHoy = data?.iTiempoRespuestaPromedioHoy ?? 0;
  const tiempoRespuesta3Dias = data?.iTiempoRespuestaPromedio3Dias ?? 0;
  const tiempoRespuesta7Dias = data?.iTiempoRespuestaPromedio7Dias ?? 0;
  const tiempoCierreHoy = data?.iTiempoCierrePromedioHoy ?? 0;
  const tiempoCierre3Dias = data?.iTiempoCierrePromedio3Dias ?? 0;
  const tiempoCierre7Dias = data?.iTiempoCierrePromedio7Dias ?? 0;

  // Cálculo de tendencia vs ayer
  let trendSubtext = 'Registrados hoy';
  let trendIcon = null;
  let trendColor = '#64748b';

  if (totalAyer > 0) {
    const diffPct = Math.round(((totalHoy - totalAyer) / totalAyer) * 100);
    trendSubtext = `${diffPct >= 0 ? '+' : ''}${diffPct}% vs ayer`;
    trendIcon = diffPct >= 0 ? faArrowTrendUp : faArrowTrendDown;
    trendColor = diffPct >= 0 ? '#10b981' : '#ef4444';
  } else if (totalHoy > 0) {
    trendSubtext = '+100% vs ayer';
    trendIcon = faArrowTrendUp;
    trendColor = '#10b981';
  }

  const cards = [
    {
      label: 'TOTALES HOY',
      value: totalHoy.toLocaleString(),
      icon: faHashtag,
      badgeClass: 'green',
      topBorderClass: 'accent-top-green',
      subtext: totalHistorico > 0 ? `${trendSubtext} • ${totalHistorico.toLocaleString()} histórico` : trendSubtext,
      subtextTrendIcon: trendIcon,
      subtextColor: trendColor
    },
    {
      label: 'PENDIENTES',
      value: pendientes.toLocaleString(),
      icon: faCalendarAlt,
      badgeClass: 'red',
      topBorderClass: 'accent-top-red',
      subtext: pendientes > 0 ? 'Requiere atención inmediata' : 'Al día, sin pendientes',
      subtextTrendIcon: null,
      subtextColor: pendientes > 0 ? '#ef4444' : '#10b981'
    },
    {
      label: 'EN PROGRESO',
      value: enProgreso.toLocaleString(),
      icon: faTachometerAlt,
      badgeClass: 'blue',
      topBorderClass: 'accent-top-blue',
      subtext: enProgreso > 0 ? 'En atención activa' : 'Sin tickets en atención',
      subtextTrendIcon: null,
      subtextColor: '#0284c7'
    },
    {
      label: 'RESUELTOS',
      value: resueltosHoy.toLocaleString(),
      icon: faCheckCircle,
      badgeClass: 'green',
      topBorderClass: 'accent-top-green',
      subtext: totalResueltos > 0 ? `${totalResueltos.toLocaleString()} acumulados en total` : 'Cerrados hoy',
      subtextTrendIcon: null,
      subtextColor: '#059669'
    }
  ];


  return (
    <>
      {/* 4 Métricas Principales en cuadrícula: 2x2 en móvil (xs={6}) / 4 columnas en desktop (xl={3}) */}
      <Row className="g-2 g-sm-3 mb-3">
        {cards.map((m, idx) => (
          <Col key={idx} xs={6} xl={3}>
            <div className={`se-metric-card ${m.topBorderClass} h-100 d-flex flex-column justify-content-between`}>
              <div>
                <div className="d-flex justify-content-between align-items-center mb-1">
                  <span className="se-metric-label">{m.label}</span>
                  <div className={`se-metric-icon-badge ${m.badgeClass}`}>
                    <FontAwesomeIcon icon={m.icon} />
                  </div>
                </div>

                {m.label === 'EN PROGRESO' && enProgreso > 0 ? (
                  /* Layout Lado a Lado: Total a la izquierda y Top 3 Tickets con Ver todos abajo a la derecha */
                  <div className="d-flex align-items-start gap-3 mt-1">
                    {/* Columna Izquierda: Total */}
                    <div className="flex-shrink-0" style={{ minWidth: '55px' }}>
                      <div className="se-metric-value mb-0">
                        {isLoading ? (
                          <Spinner animation="border" size="sm" variant="secondary" />
                        ) : (
                          m.value
                        )}
                      </div>
                    </div>

                    {/* Columna Derecha: Top 3 tickets con mayor tiempo y botón Ver todos abajo */}
                    <div className="flex-grow-1" style={{ minWidth: 0 }}>
                      {isLoadingTickets ? (
                        <div className="py-2 text-center text-muted" style={{ fontSize: '0.75rem' }}>
                          <Spinner animation="border" size="sm" variant="secondary" className="me-1" />
                        </div>
                      ) : inProgressTickets.length > 0 ? (
                        <div className="se-card-tickets-preview">
                          {inProgressTickets.slice(0, 3).map(t => {
                            const dur = t.iTiempoTranscurridoMinutos || 0;
                            const timeClass = dur >= 180 ? 'danger' : dur >= 60 ? 'warning' : 'normal';
                            return (
                              <div
                                key={t.idTicket}
                                className="se-card-ticket-item"
                                style={{ cursor: 'pointer' }}
                                onClick={() => handleOpenTicket(t.idTicket)}
                                title={`Ticket #${t.idTicket}: ${t.vCustomerName || 'Cliente'} - Clic para abrir en nueva pestaña`}
                              >
                                <span className="se-card-ticket-id">#{t.idTicket}</span>
                                <span className="se-card-ticket-name">
                                  {t.vCustomerName || t.vAsunto || 'Cliente'}
                                </span>
                                <span className={`se-card-ticket-time ${timeClass}`}>
                                  <FontAwesomeIcon icon={faClock} className="me-1" style={{ fontSize: '0.60rem' }} />
                                  {formatDuration(dur)}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      ) : null}

                      {/* Botón Ver todos ubicado directamente abajo de los 3 tickets */}
                      <div className="mt-2">
                        <button
                          type="button"
                          className="se-card-view-all-btn"
                          onClick={() => setShowModal(true)}
                        >
                          <span>Ver todos ({totalRegistros || inProgressTickets.length || enProgreso})</span>
                          <FontAwesomeIcon icon={faArrowRight} className="ms-1" style={{ fontSize: '0.65rem' }} />
                        </button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="se-metric-value">
                      {isLoading ? (
                        <Spinner animation="border" size="sm" variant="secondary" />
                      ) : (
                        m.value
                      )}
                    </div>
                    <div className="se-metric-subtext mt-1" style={{ color: m.subtextColor }}>
                      {m.subtextTrendIcon && (
                        <FontAwesomeIcon icon={m.subtextTrendIcon} className="me-1" />
                      )}
                      {m.subtext}
                    </div>
                  </>
                )}
              </div>
            </div>
          </Col>
        ))}
      </Row>

      {/* 2 Métricas de Tiempo con Gráficas por Hora: 1 columna en móvil (xs={12}) / 2 columnas en desktop (lg={6}) */}
      <Row className="g-2 g-sm-3 mb-4">
        <Col xs={12} lg={6}>
          <TimeMetricCard
            label="TIEMPO 1RA RESPUESTA (PROM)"
            icon={faStopwatch}
            iconColor="#6366f1"
            lineColor="#6366f1"
            accentClass="accent-purple"
            valueHoy={tiempoRespuestaHoy}
            value3Dias={tiempoRespuesta3Dias}
            value7Dias={tiempoRespuesta7Dias}
            metricKey="iTiempoRespuestaPromedio"
            countKey="iTotalRespuesta"
            agentId={agentId}
            isLoading={isLoading}
          />
        </Col>
        <Col xs={12} lg={6}>
          <TimeMetricCard
            label="TIEMPO RESOLUCIÓN / CIERRE (PROM)"
            icon={faClock}
            iconColor="#0284c7"
            lineColor="#0284c7"
            accentClass="accent-cyan"
            valueHoy={tiempoCierreHoy}
            value3Dias={tiempoCierre3Dias}
            value7Dias={tiempoCierre7Dias}
            metricKey="iTiempoCierrePromedio"
            countKey="iTotalCerrados"
            agentId={agentId}
            isLoading={isLoading}
          />
        </Col>
      </Row>

      {/* Modal con todos los tickets en progreso ordenados por mayor tiempo */}
      <InProgressTicketsModal
        show={showModal}
        onHide={() => setShowModal(false)}
        tickets={inProgressTickets}
        isLoading={isLoadingTickets}
        page={page}
        totalPages={totalPages}
        totalCount={totalRegistros}
        onNextPage={goToNextPage}
        onPrevPage={goToPrevPage}
        onGoToPage={goToPage}
      />
    </>
  );
};

export default MetricsCards;
